import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  buildReturnToFromLocation,
  navigateWithReturn
} from '../../../../shared/navigation';
import { useQuery } from '@tanstack/react-query';
import { getOverview } from '../../../services/api/implementation/impl';
import TabComponents from '../../../../shared/components/TabComponent';
import { formatIndianCurrency } from '../../../../shared/utils/CurrencyFormat';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { useProjectsList } from '../../../../../contexts/ProjectsContext';

const formatCurrencyValue = (value) => {
  const num = Number(value);
  if (value == null || value === '' || Number.isNaN(num)) return 'N/A';
  return formatIndianCurrency(num);
};

const OverviewInternalTable = ({ headers, rows, onRowClick }) => {
  const columnCount = headers.length;

  return (
    <div className="w-full h-full bg-white border border-[#E7EBF1] rounded-xl overflow-hidden flex flex-col">
      <div
        className="grid bg-[#F8FAFC] border-b border-[#E7EBF1] text-[#64748B] text-sm font-semibold shrink-0"
        style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}>
        {headers.map((header) => (
          <div key={header} className="px-4 py-3 text-left">
            {header}
          </div>
        ))}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
        {rows?.length > 0 ? (
          rows.map((row) => (
            <div
              key={row.id}
              role={onRowClick ? 'button' : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onClick={onRowClick ? () => onRowClick(row.id) : undefined}
              onKeyDown={
                onRowClick
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onRowClick(row.id);
                      }
                    }
                  : undefined
              }
              className={`grid border-b border-[#EEF2F6] text-sm text-gray-800 last:border-b-0 ${
                onRowClick ? 'cursor-pointer hover:bg-[#F8FAFC]' : ''
              }`}
              style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}>
              {row.cells.map((cell, idx) => (
                <div key={`${row.id}-${idx}`} className="px-4 py-3 text-left">
                  {cell}
                </div>
              ))}
            </div>
          ))
        ) : (
          <div className="px-4 py-8 text-center text-sm text-[#64748B]">No data available</div>
        )}
      </div>
    </div>
  );
};
const Overview = () => {
  const userId = Cookies.get('userId');
  const navigate = useNavigate();
  const location = useLocation();
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  // Track page view
  useEffect(() => {
    mixpanel.track('Super Admin Overview Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  // Fetching overview data
  const { data: overview } = useQuery({
    queryKey: ['overview'],
    queryFn: () => getOverview(),
    onSuccess: (result) => {
      // Check if result is an error object
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        mixpanel.track('Super Admin Overview Data Fetch Failed', {
          user_id: userId,
          error_message: result?.response?.data?.message || result?.message || 'Unknown error',
          timestamp: new Date().toISOString()
        });
        return;
      }

      mixpanel.track('Super Admin Overview Data Fetch Success', {
        user_id: userId,
        revenue_entries_count: Object.keys(result || {}).length,
        timestamp: new Date().toISOString()
      });
    },
    onError: (err) => {
      mixpanel.track('Super Admin Overview Data Fetch Failed', {
        user_id: userId,
        error_message: err?.response?.data?.message || err?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  console.log({ overview, projects });

  const revenueRows =
    overview &&
    Object.values(overview)?.map((item) => {
      const id = `${item?.builder?.id}-${item?.company?.id}-${item?.project?.id}`;
      return {
        id,
        item,
        cells: [
          item?.builder?.name || '—',
          item?.company?.company_name || '—',
          item?.project?.name || '—',
          formatCurrencyValue(item.amount)
        ]
      };
    });

  const budgetRows = projects?.map((item, index) => ({
    id: `${item.id ?? index}`,
    cells: [
      item.company?.company_name || '—',
      item.name || '—',
      formatCurrencyValue(item.buildings?.[0]?.estimated_budget)
    ]
  }));

  // State for filtering tabs
  const [filter, setFilter] = useState('revenue');

  const handleRevenueTabClick = () => {
    mixpanel.track('Super Admin Overview Tab Switched', {
      user_id: userId,
      from_tab: filter,
      to_tab: 'revenue',
      timestamp: new Date().toISOString()
    });
    setFilter('revenue');
  };

  const handleBudgetTabClick = () => {
    mixpanel.track('Super Admin Overview Tab Switched', {
      user_id: userId,
      from_tab: filter,
      to_tab: 'budget',
      timestamp: new Date().toISOString()
    });
    setFilter('budget');
  };

  const handleRevenueRowClick = (compositeId) => {
    const [builderId, companyId, projectId] = compositeId.split('-');
    const revenueRow = revenueRows?.find((r) => r.id === compositeId);
    const revenueItem = revenueRow?.item;

    mixpanel.track('Super Admin Revenue Row Clicked', {
      user_id: userId,
      builder_id: builderId,
      company_id: companyId,
      project_id: projectId,
      timestamp: new Date().toISOString()
    });

    navigateWithReturn(
      navigate,
      `/super-admin/billings-payment/revenue/${builderId}/${companyId}/${projectId}`,
      {
        returnTo: buildReturnToFromLocation(location),
        state: {
          builderName: revenueItem?.builder?.name,
          companyName: revenueItem?.company?.company_name,
          projectName: revenueItem?.project?.name,
          totalRevenue: revenueItem?.amount
        }
      }
    );
  };

  return (
    <div className="flex flex-col h-[calc(100vh-14rem)]">
      <div className="flex items-center justify-between px-3 py-2 bg-white border-t-[2px] border-stone-100 shadow-sm z-10 shrink-0">
        <TabComponents
          leftButtonText="Revenue Generated"
          rightButtonText="Budget Allocated"
          onLeftButtonClick={handleRevenueTabClick}
          onRightButtonClick={handleBudgetTabClick}
        />
      </div>
      <div className="flex-1 min-h-0 px-6 pt-3">
        {filter === 'budget' ? (
          <OverviewInternalTable
            headers={['Company name', 'Project name', 'Budget (In Lakh)']}
            rows={budgetRows}
          />
        ) : (
          <OverviewInternalTable
            headers={['Builder Name', 'Company name', 'Project name', 'Revenue']}
            rows={revenueRows}
            onRowClick={handleRevenueRowClick}
          />
        )}
      </div>
    </div>
  );
};

export default Overview;
